/**
 * CANONICAL MODULE — app-platform/shared/storagePersist.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/storagePersist.ts`). It imports nothing
 * from any app; the only app-specific fact, the log label, is passed in by the caller.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place** — that is exactly how
 * `bundle-budget.mjs` became three different files, each with a safeguard the other two never got.
 * `errorLog.ts` / `errorFlush.ts` / `expectations.ts` prove the convention works: byte-identical
 * across five apps for months, verified by md5.
 *
 * ── What it is for ───────────────────────────────────────────────────────────────────────────────
 * Browsers may evict IndexedDB under disk pressure. For five apps whose local database is the only
 * copy of the user's work between syncs, that is a silent, total, unrecoverable loss, and it is the
 * cheapest one in the estate to prevent: `navigator.storage.persist()` asks the browser to exempt
 * this origin from eviction, and an installed PWA is normally granted it without prompting.
 *
 * Call `requestPersistentStorage(APP_ID)` once at boot. Everything else here exists so a Settings
 * panel can show the state without asking again.
 */

export interface PersistResult {
  /** Whether storage is now persistent (already-granted counts). */
  persisted: boolean
  /** Whether the Storage Persistence API exists in this browser at all. */
  supported: boolean
}

/**
 * Ask the browser to make this origin's storage persistent. Idempotent-friendly: if it is already
 * persisted we don't re-prompt. Never throws — a failure is logged and recorded as not-persisted.
 * Returns the outcome; isStoragePersisted reads the live state later without prompting.
 */
export async function requestPersistentStorage(appLabel = 'app'): Promise<PersistResult> {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined
  if (!storage?.persist) {
    return { persisted: false, supported: false }
  }
  try {
    const already = storage.persisted ? await storage.persisted() : false
    const persisted = already || await storage.persist()
    console.info(`[${appLabel}] persistent storage ${persisted ? 'granted' : 'not granted'}`)
    return { persisted, supported: true }
  } catch (err) {
    console.warn(`[${appLabel}] persistent storage request failed`, err)
    return { persisted: false, supported: true }
  }
}

/**
 * Read the current persistence state WITHOUT prompting (unlike `persist()`). For a Settings panel
 * showing live status. False if the API is missing or the check throws.
 */
export async function isStoragePersisted(): Promise<boolean> {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined
  if (!storage?.persisted) return false
  try {
    return await storage.persisted()
  } catch {
    return false
  }
}

export interface StorageUsage {
  /** Whether the StorageManager estimate API exists here. */
  supported: boolean
  /** Bytes this origin is currently using (IndexedDB + Cache + SW), if known. */
  usage?: number
  /** The browser's estimated quota for this origin, if known. */
  quota?: number
}

/** Best-effort origin storage estimate for a health panel. Never throws. */
export async function getStorageEstimate(): Promise<StorageUsage> {
  const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined
  if (!storage?.estimate) return { supported: false }
  try {
    const { usage, quota } = await storage.estimate()
    return { supported: true, usage, quota }
  } catch {
    return { supported: false }
  }
}

/** Human-readable byte size (binary units), e.g. "0 B", "512 KB", "4.2 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1 }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[i]}`
}
